import * as THREE from 'three';
import { Spell, SpellConfig } from './Spell';

/**
 * Meteor - Ultimate two-handed spell (both hands draw circles).
 * Massive flaming boulder with heavy smoke trail, screen-shaking impact,
 * and large scorch mark. 2x charge time, devastating damage.
 */
export class MeteorSpell extends Spell {
  private core!: THREE.Mesh;
  private innerGlow!: THREE.Mesh;
  private outerGlow!: THREE.Mesh;
  private smokeTrail!: THREE.Points;
  private smokePositions!: Float32Array;
  private emberTrail!: THREE.Points;
  private emberPositions!: Float32Array;
  private impactAnimationId: number | null = null;

  constructor(
    scene: THREE.Scene,
    startPos: THREE.Vector3,
    targetPos: THREE.Vector3,
    damage: number,
    isPlayerSpell: boolean = true,
    chargeTime: number = 2,
  ) {
    const config: SpellConfig = {
      type: 'meteor',
      damage,
      speed: 10, // Slower than fireball - massive projectile
      color: new THREE.Color(0xFF4500),
      particleCount: 80,
      chargeTime,
    };
    super(scene, startPos, targetPos, config, isPlayerSpell);
  }

  protected createSpellVisual(): void {
    // Large rocky core
    const coreGeometry = new THREE.IcosahedronGeometry(0.7, 1);
    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0x8B4513,
      transparent: true,
      opacity: 1,
    });
    this.core = new THREE.Mesh(coreGeometry, coreMaterial);
    this.mesh.add(this.core);

    // Molten inner glow
    const innerGlowGeometry = new THREE.SphereGeometry(0.85, 16, 16);
    const innerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFAA00,
      transparent: true,
      opacity: 0.7,
    });
    this.innerGlow = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial);
    this.mesh.add(this.innerGlow);

    // Outer fire glow
    const outerGlowGeometry = new THREE.SphereGeometry(1.2, 16, 16);
    const outerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFF4500,
      transparent: true,
      opacity: 0.35,
    });
    this.outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
    this.mesh.add(this.outerGlow);

    this.createSmokeTrail();
    this.createEmberTrail();
  }

  private createSmokeTrail(): void {
    const trailCount = 50;
    this.smokePositions = new Float32Array(trailCount * 3);
    for (let i = 0; i < trailCount * 3; i++) {
      this.smokePositions[i] = this.mesh.position.x;
    }

    const trailGeometry = new THREE.BufferGeometry();
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(this.smokePositions, 3));

    const trailMaterial = new THREE.PointsMaterial({
      color: 0x444444,
      size: 0.4,
      transparent: true,
      opacity: 0.5,
      blending: THREE.NormalBlending,
    });

    this.smokeTrail = new THREE.Points(trailGeometry, trailMaterial);
    this.scene.add(this.smokeTrail);
  }

  private createEmberTrail(): void {
    const emberCount = 35;
    this.emberPositions = new Float32Array(emberCount * 3);
    for (let i = 0; i < emberCount * 3; i++) {
      this.emberPositions[i] = this.mesh.position.x;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.emberPositions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xFF6B35,
      size: 0.2,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
    });

    this.emberTrail = new THREE.Points(geometry, material);
    this.scene.add(this.emberTrail);
  }

  protected createParticles(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.config.particleCount * 3);
    const colors = new Float32Array(this.config.particleCount * 3);

    for (let i = 0; i < this.config.particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const r = 0.5 + Math.random() * 0.5;

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      // Fire color palette: white-yellow core to deep red-orange
      const t = Math.random();
      if (t < 0.2) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 0.6;
      } else if (t < 0.5) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 0.5 + Math.random() * 0.3; colors[i * 3 + 2] = 0;
      } else {
        colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.15 + Math.random() * 0.2; colors[i * 3 + 2] = 0;
      }
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geometry, material);
    this.mesh.add(particles);
    return particles;
  }

  protected updateParticles(_delta: number): void {
    if (!this.particles || !this.particles.geometry.attributes.position) return;

    const positions = this.particles.geometry.attributes.position.array as Float32Array;

    for (let i = 0; i < this.config.particleCount; i++) {
      positions[i * 3] += (Math.random() - 0.5) * 0.12;
      positions[i * 3 + 1] += (Math.random() - 0.5) * 0.12;
      positions[i * 3 + 2] += (Math.random() - 0.5) * 0.12;

      const dist = Math.sqrt(
        positions[i * 3] ** 2 +
        positions[i * 3 + 1] ** 2 +
        positions[i * 3 + 2] ** 2,
      );

      if (dist > 0.8) {
        positions[i * 3] *= 0.6 / dist;
        positions[i * 3 + 1] *= 0.6 / dist;
        positions[i * 3 + 2] *= 0.6 / dist;
      }
    }

    this.particles.geometry.attributes.position.needsUpdate = true;

    // Pulsing glow
    if (this.innerGlow) {
      this.innerGlow.scale.setScalar(1 + Math.sin(Date.now() * 0.008) * 0.15);
    }
    if (this.outerGlow) {
      this.outerGlow.scale.setScalar(1 + Math.sin(Date.now() * 0.006) * 0.1);
    }

    // Rotate the rocky core
    if (this.core) {
      this.core.rotation.x += _delta * 1.5;
      this.core.rotation.y += _delta * 2;
    }

    // Update smoke trail
    if (this.smokeTrail && this.smokePositions) {
      const trailCount = this.smokePositions.length / 3;
      for (let i = trailCount - 1; i > 0; i--) {
        this.smokePositions[i * 3] = this.smokePositions[(i - 1) * 3] + (Math.random() - 0.5) * 0.1;
        this.smokePositions[i * 3 + 1] = this.smokePositions[(i - 1) * 3 + 1] + Math.random() * 0.05;
        this.smokePositions[i * 3 + 2] = this.smokePositions[(i - 1) * 3 + 2] + (Math.random() - 0.5) * 0.1;
      }
      this.smokePositions[0] = this.mesh.position.x;
      this.smokePositions[1] = this.mesh.position.y;
      this.smokePositions[2] = this.mesh.position.z;
      this.smokeTrail.geometry.attributes.position.needsUpdate = true;
    }

    // Update ember trail
    if (this.emberTrail && this.emberPositions) {
      const emberCount = this.emberPositions.length / 3;
      for (let i = emberCount - 1; i > 0; i--) {
        this.emberPositions[i * 3] = this.emberPositions[(i - 1) * 3] + (Math.random() - 0.5) * 0.15;
        this.emberPositions[i * 3 + 1] = this.emberPositions[(i - 1) * 3 + 1] + (Math.random() - 0.5) * 0.15;
        this.emberPositions[i * 3 + 2] = this.emberPositions[(i - 1) * 3 + 2] + (Math.random() - 0.5) * 0.1;
      }
      this.emberPositions[0] = this.mesh.position.x + (Math.random() - 0.5) * 0.3;
      this.emberPositions[1] = this.mesh.position.y + (Math.random() - 0.5) * 0.3;
      this.emberPositions[2] = this.mesh.position.z;
      this.emberTrail.geometry.attributes.position.needsUpdate = true;
    }
  }

  public createImpactEffect(): void {
    this.emitEvent('impact', { position: this.impactPosition.toArray(), type: 'meteor' });

    const explosionParticleCount = 300; // Massive explosion
    const positions = new Float32Array(explosionParticleCount * 3);
    const colors = new Float32Array(explosionParticleCount * 3);
    const velocities: THREE.Vector3[] = [];
    const impactPos = this.impactPosition;

    for (let i = 0; i < explosionParticleCount; i++) {
      positions[i * 3] = impactPos.x;
      positions[i * 3 + 1] = impactPos.y;
      positions[i * 3 + 2] = impactPos.z;

      const t = i / explosionParticleCount;
      if (t < 0.15) {
        // White-hot core
        colors[i * 3] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 0.8;
      } else if (t < 0.4) {
        // Orange flames
        colors[i * 3] = 1; colors[i * 3 + 1] = 0.4 + Math.random() * 0.3; colors[i * 3 + 2] = 0;
      } else if (t < 0.7) {
        // Dark red embers
        colors[i * 3] = 0.8; colors[i * 3 + 1] = 0.1 + Math.random() * 0.15; colors[i * 3 + 2] = 0;
      } else {
        // Dark smoke/debris
        const g = 0.2 + Math.random() * 0.15;
        colors[i * 3] = g; colors[i * 3 + 1] = g; colors[i * 3 + 2] = g;
      }

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 6 + Math.random() * 14; // Faster/wider explosion
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.sin(phi) * Math.sin(theta) * speed * 0.6 + 3,
        Math.cos(phi) * speed,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.35,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const explosion = new THREE.Points(geometry, material);
    this.scene.add(explosion);

    // Huge flash light
    const flashLight = new THREE.PointLight(0xFF6B35, 8, 20);
    flashLight.position.copy(impactPos);
    this.scene.add(flashLight);

    // Ground scorch ring
    const scorchGeo = new THREE.RingGeometry(0.5, 2.5, 32);
    const scorchMat = new THREE.MeshBasicMaterial({
      color: 0x8B4513,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    const scorch = new THREE.Mesh(scorchGeo, scorchMat);
    scorch.position.set(impactPos.x, 0.05, impactPos.z);
    scorch.rotation.x = -Math.PI / 2;
    this.scene.add(scorch);

    // Expanding shockwave ring
    const shockwaveGeo = new THREE.RingGeometry(0.3, 1.2, 32);
    const shockwaveMat = new THREE.MeshBasicMaterial({
      color: 0xFF8800,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });
    const shockwave = new THREE.Mesh(shockwaveGeo, shockwaveMat);
    shockwave.position.copy(impactPos);
    shockwave.lookAt(this.startPosition);
    this.scene.add(shockwave);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = explosion.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < explosionParticleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 8 * 0.016; // Heavier gravity for meteor debris
      }
      explosion.geometry.attributes.position.needsUpdate = true;

      material.opacity = Math.max(0, 1 - (elapsed / 1.2));
      flashLight.intensity = 8 * Math.max(0, 1 - (elapsed / 0.4));
      shockwave.scale.setScalar(1 + elapsed * 10);
      shockwaveMat.opacity = 0.7 * Math.max(0, 1 - (elapsed / 0.5));

      // Fade scorch mark slowly
      scorchMat.opacity = 0.8 * Math.max(0, 1 - (elapsed / 3));

      if (elapsed < 1.5) {
        this.impactAnimationId = requestAnimationFrame(animate);
      } else {
        this.impactAnimationId = null;
        this.scene.remove(explosion);
        this.scene.remove(flashLight);
        this.scene.remove(shockwave);
        this.scene.remove(scorch);
        geometry.dispose();
        material.dispose();
        flashLight.dispose();
        shockwaveGeo.dispose();
        shockwaveMat.dispose();
        scorchGeo.dispose();
        scorchMat.dispose();
      }
    };

    this.impactAnimationId = requestAnimationFrame(animate);
  }

  dispose(): void {
    if (this.impactAnimationId !== null) {
      cancelAnimationFrame(this.impactAnimationId);
      this.impactAnimationId = null;
    }
    super.dispose();
    if (this.smokeTrail) {
      this.scene.remove(this.smokeTrail);
      this.smokeTrail.geometry.dispose();
      (this.smokeTrail.material as THREE.Material).dispose();
    }
    if (this.emberTrail) {
      this.scene.remove(this.emberTrail);
      this.emberTrail.geometry.dispose();
      (this.emberTrail.material as THREE.Material).dispose();
    }
  }
}
